/**
 * Handbuch der Unterlagen-Checkliste — pure Regeln.
 *
 * Das Handbuch ist die Checkliste als PDF, aufgebaut wie der Reiter
 * „Unterlagen", mit einem ausfuellbaren Notizfeld je Unterlage. Wer es am
 * Rechner ausfuellt, kann die Notizen wieder einlesen; sie landen in der
 * vorhandenen `bemerkung` des Eintrags (keine neue Spalte).
 *
 * Hier steht, was ohne jsPDF, pdf-lib und DOM entscheidbar ist: das
 * Blattmodell (was steht in welcher Reihenfolge auf dem Blatt), die
 * Feldnamen und der Abgleich beim Einlesen. Gezeichnet und gelesen wird in
 * `unterlagenHandbuchPdf.ts`.
 *
 * ═══ DREI STAENDE JE NOTIZ ═══════════════════════════════════════════════
 *   ausgang — die Bemerkung beim Export (steht unsichtbar im PDF)
 *   neu     — was jetzt im Notizfeld des PDF steht
 *   aktuell — die Bemerkung jetzt in der App
 * Nur so laesst sich unterscheiden, ob im PDF etwas geaendert wurde, ob in
 * der App inzwischen etwas geaendert wurde, oder beides. Ohne den
 * Ausgangsstand ueberschriebe jedes Einlesen still, was ein Kollege in der
 * Zwischenzeit in der App eingetragen hat.
 */

import { ART_TITEL, GRUNDSATZ_ART_C, LIEFERANT_TITEL } from '../../services/mitgeltendeDokumente';
import type { FrageAdressat, PlanungUnterlage, UnterlagenStatus } from './types';
import {
    berechneUnterlagenFortschritt,
    datumDeutsch,
    eintragFuerZeile,
    gruppiereUnterlagen,
    merkmalText,
    setzeUnterlagenBemerkung,
    uebernehmeUnterlage,
    type UnterlagenFilter,
    type UnterlagenZeile,
} from './unterlagenLogic';

// ═══════════════════════════════════════════════════════════════════════════
// KENNUNGEN IM PDF
// ═══════════════════════════════════════════════════════════════════════════

/** Fassung des Handbuch-Formats. Hochzaehlen, wenn sich Feldnamen oder
 *  Metadaten so aendern, dass alte Handbuecher anders gelesen werden muessten. */
export const HANDBUCH_FORMAT = 1;

export const HANDBUCH_TITEL = 'Unterlagenliste – Mitgeltende Dokumente';

/** Unsichtbare, schreibgeschuetzte Felder mit den Metadaten. Felder statt
 *  Info-Dictionary: ein Formularfeld uebersteht das Speichern in jedem
 *  PDF-Programm, das Formulare kann — das Info-Dictionary nicht zuverlaessig. */
export const FELD_META = 'handbuch:meta';
export const FELD_AUSGANG = 'handbuch:ausgang';

const NOTIZ_PRAEFIX = 'notiz:';
const ERLAUBT = /^[A-Za-z0-9_:-]$/;

/**
 * Feldname der Notiz einer Zeile: `notiz:<zeilenId>`.
 *
 * Die Kennung wird prozentkodiert, soweit sie andere Zeichen als
 * `A–Z a–z 0–9 _ : -` enthaelt. Grund: ein PUNKT trennt in PDF-Feldnamen die
 * Ebenen — `plan:statik fa. muster` wuerde zu zwei verschachtelten Feldern —,
 * und Umlaute oder Leerzeichen im Feldnamen behandeln nicht alle
 * PDF-Programme gleich. `eigen:<uuid>` und Katalog-Ids bleiben lesbar.
 */
export const notizFeldName = (id: string): string => {
    let out = '';
    for (const ch of String(id ?? '')) {
        if (ERLAUBT.test(ch)) { out += ch; continue; }
        out += Array.from(new TextEncoder().encode(ch))
            .map(b => `%${b.toString(16).toUpperCase().padStart(2, '0')}`)
            .join('');
    }
    return `${NOTIZ_PRAEFIX}${out}`;
};

/** Zeilen-Id aus einem Feldnamen, `null` fuer fremde oder kaputte Namen. */
export const idAusNotizFeld = (feld: string): string | null => {
    const name = String(feld ?? '');
    if (!name.startsWith(NOTIZ_PRAEFIX)) return null;
    const roh = name.slice(NOTIZ_PRAEFIX.length);
    if (!roh) return null;
    try {
        return decodeURIComponent(roh);
    } catch {
        return null;
    }
};

export interface HandbuchMeta {
    format: number;
    phaseId: string;
    projectId: string;
    /** Zeitpunkt des Exports (ISO). */
    stand: string;
}

/**
 * Text einer Notiz fuer Vergleiche: einheitliche Zeilenenden (PDF-Programme
 * speichern Feldwerte teils mit `\r`), ohne Leerraum am Zeilenende und an den
 * Raendern. Ein Programm, das beim Speichern nur einen Zeilenumbruch anhaengt,
 * soll keine „Aenderung" erzeugen.
 */
export const normalisiereNotiz = (text: unknown): string =>
    String(text ?? '')
        .replace(/\r\n?/g, '\n')
        .replace(/[ \t]+$/gm, '')
        .trim();

// ═══════════════════════════════════════════════════════════════════════════
// BLATTMODELL
// ═══════════════════════════════════════════════════════════════════════════

export const HANDBUCH_UNTERTITEL = 'Handbuch zur Planungsphase (§ 3 Abs. 2 BaustellV)';

export const FILTER_TEXT: Record<UnterlagenFilter, string> = {
    relevant: 'Relevante Unterlagen',
    offen: 'Offene Unterlagen',
    alle: 'Alle Unterlagen des Katalogs',
};

/** Status als Kuerzel fuer die Tabellenspalte; erklaert in der Legende. */
export const STATUS_KUERZEL: Record<UnterlagenStatus, string> = {
    offen: 'O',
    angefordert: 'A',
    liegt_vor: 'V',
    nicht_erforderlich: 'E',
};

const LIEFERANT_REIHENFOLGE: FrageAdressat[] = ['BH', 'OP', 'TWP', 'TGA', 'FP', 'GUT', 'BEH', 'AN', 'BET'];

/** Eine Position der Tabelle — eine Unterlage. */
export interface HandbuchBlock {
    id: string;
    feldName: string;
    /** Positionsnummer „1.01". */
    pos: string;
    /** Name, ggf. mit Kurzform. */
    name: string;
    /** Fachpraxis statt Maengelkatalog — Fussnotenzeichen am Namen. */
    fachpraxis: boolean;
    art: 'A' | 'B' | '';
    /** Lieferanten als Kuerzel, „GUT, BH". */
    lieferant: string;
    /** Kuerzel und Datum, „A 01.10.26". */
    status: string;
    /** Erlaeuterungszeilen unter dem Namen, je mit Bezeichnung. */
    erlaeuterung: string[];
    /** Vorbelegung des Notizfelds = aktuelle Bemerkung. */
    notiz: string;
}

export interface HandbuchGruppe {
    nummer: number;
    titel: string;
    bloecke: HandbuchBlock[];
}

/** Die Felder des Protokollkopfs. Leer = nicht erfasst. */
export interface HandbuchKopf {
    bauvorhaben: string;
    projektNummer: string;
    bauherr: string;
    stand: string;
    aufgestelltVon: string;
    ansicht: string;
}

export interface HandbuchZaehlung {
    /** Relevante Unterlagen ohne die entfallenen. */
    relevant: number;
    liegtVor: number;
    angefordert: number;
    offen: number;
    entfaellt: number;
    /** Erforderlich (Merkmal oder SiGe-Plan) und noch nicht angefordert. */
    erforderlichNichtAngefordert: number;
}

export interface HandbuchModell {
    titel: string;
    untertitel: string;
    standIso: string;
    kopf: HandbuchKopf;
    zaehlung: HandbuchZaehlung;
    /** Legende und Vorbemerkung — Bezeichnung und Text je Zeile. */
    legende: { label: string; text: string }[];
    gruppen: HandbuchGruppe[];
    meta: HandbuchMeta;
    /** Ausgangsstand je Zeilen-Id — die exportierte Bemerkung. */
    ausgang: Record<string, string>;
}

export interface HandbuchEingang {
    /** Die AKTUELL GEFILTERTE Ansicht — sie bestimmt, was auf dem Blatt steht. */
    zeilen: readonly UnterlagenZeile[];
    /** Alle Zeilen — fuer die Zaehlung im Kopf (wie in der App). */
    alleZeilen: readonly UnterlagenZeile[];
    filter: UnterlagenFilter;
    suche?: string;
    standIso: string;
    phaseId: string;
    projectId: string;
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    /** Bauherr aus dem aktuellen SiGe-Plan, sofern bekannt. */
    bauherr?: string;
    /** Wer die Liste aufstellt (angemeldeter Nutzer). */
    aufgestelltVon?: string;
    /** Titel verknuepfbarer Belege der Ablage (ohne admin-gesperrte). */
    belegTitel: ReadonlyMap<string, string>;
    /** `false`, wenn die Ablage nicht gelesen werden konnte — dann laesst
     *  sich „nicht verfuegbar" nicht behaupten. Vorgabe: `true`. */
    belegeBekannt?: boolean;
}

/** ISO → TT.MM.JJ fuer die schmale Statusspalte. */
const datumKurz = (iso?: string | null): string => {
    const lang = datumDeutsch(iso);
    return lang ? `${lang.slice(0, 6)}${lang.slice(8)}` : '';
};

/**
 * Status als Kuerzel mit dem Datum, das ihn belegt: bei „angefordert" das
 * ERSTE Anforderungsdatum, bei „liegt vor" und „entfaellt" der Zeitpunkt
 * des Statuswechsels.
 */
export const statusKurz = (z: UnterlagenZeile): string => {
    const e = z.eintrag;
    const kuerzel = STATUS_KUERZEL[z.status];
    let datum: string | null = null;
    if (e && z.status === 'angefordert') datum = e.angefordert_am;
    else if (e && (z.status === 'liegt_vor' || z.status === 'nicht_erforderlich')) datum = e.status_am;
    const d = datumKurz(datum);
    return d ? `${kuerzel} ${d}` : kuerzel;
};

/**
 * Die Erlaeuterung unter dem Namen. Katalogdokumente: Ausloeser, Bedeutung
 * fuer den SiGeKo, Vorlauf, Rechtsgrundlage; dazu bei jeder Zeile knapp die
 * Relevanz und ein verknuepfter Beleg.
 *
 * Der Beleg: nur Titel aus `belegTitel`. Ein Verweis auf ein Dokument, das
 * dort fehlt (geloescht oder admin-gesperrt), erscheint als „nicht
 * verfuegbar" — sein Titel darf nicht auf ein Blatt, das jeder lesen kann
 * (dieselbe Lehre wie `asplan/ablage/anlagen.ts`).
 */
export const erlaeuterungFuer = (
    z: UnterlagenZeile,
    belegTitel: ReadonlyMap<string, string>,
    belegeBekannt = true,
): string[] => {
    const d = z.dokument;
    const zeilen: string[] = [];
    if (d) {
        if (d.wannBenoetigt) zeilen.push(`Erforderlich, wenn: ${d.wannBenoetigt}`);
        if (d.sigekoRelevanz) zeilen.push(`SiGeKo: ${d.sigekoRelevanz}`);
        if (d.vorlauf) zeilen.push(`Vorlauf: ${d.vorlauf}`);
        if (d.rechtsgrundlage && d.rechtsgrundlage.length) zeilen.push(`Rechtsgrundlage: ${d.rechtsgrundlage.join('; ')}`);
    } else if (z.quelle === 'eigen') {
        zeilen.push('Eigene Unterlage');
    } else if (z.quelle === 'sigeplan') {
        zeilen.push('Aus dem SiGe-Plan, nicht im Katalog');
    } else {
        zeilen.push('Nicht mehr im Katalog');
    }

    const merkmale = z.merkmale.map(merkmalText).join(', ');
    if (z.relevanz === 'erforderlich') zeilen.push(`Relevanz: erforderlich (${merkmale})`);
    else if (z.relevanz === 'zu_klaeren') zeilen.push(`Relevanz: zu klären (offen: ${merkmale})`);
    if (z.sigeSchritte.length) zeilen.push(`SiGe-Plan: ${z.sigeSchritte.join('; ')}`);

    const belegId = z.eintrag ? z.eintrag.dokument_id : null;
    if (belegId) {
        const titel = belegTitel.get(belegId);
        if (titel) zeilen.push(`Beleg: ${titel}`);
        else if (!belegeBekannt) zeilen.push('Beleg: verknüpft (Ablage beim Erzeugen nicht lesbar)');
        else zeilen.push('Beleg: verknüpftes Dokument nicht verfügbar (gelöscht oder nur für Admins)');
    }
    return zeilen;
};

/** Eine Position der Tabelle. */
export const handbuchBlock = (
    z: UnterlagenZeile,
    pos: string,
    belegTitel: ReadonlyMap<string, string>,
    belegeBekannt = true,
): HandbuchBlock => {
    const d = z.dokument;
    const art = d && (d.art === 'A' || d.art === 'B') ? d.art : '';
    return {
        id: z.id,
        feldName: notizFeldName(z.id),
        pos,
        name: d && d.kurz ? `${z.name} (${d.kurz})` : z.name,
        fachpraxis: !!d && d.beleg === 'praxis',
        art,
        lieferant: z.lieferant.join(', '),
        status: statusKurz(z),
        erlaeuterung: erlaeuterungFuer(z, belegTitel, belegeBekannt),
        notiz: z.eintrag ? z.eintrag.bemerkung || '' : '',
    };
};

/** Legende und Vorbemerkung — erklaert jedes Kuerzel der Tabelle. */
export const handbuchLegende = (): { label: string; text: string }[] => [
    {
        label: 'Status',
        text: 'O offen · A angefordert (Datum der ersten Anforderung) · V liegt vor · E entfällt (Begründung in den Notizen)',
    },
    { label: 'Art', text: `A ${ART_TITEL.A} · B ${ART_TITEL.B}` },
    { label: 'Lieferant', text: LIEFERANT_REIHENFOLGE.map(l => `${l} ${LIEFERANT_TITEL[l]}`).join(' · ') },
    { label: '*', text: 'Fachpraxis, vor Anforderung prüfen (nicht im Mängelkatalog mit Rechtsgrundlage belegt)' },
    { label: 'Allgemeiner Hinweis', text: GRUNDSATZ_ART_C },
    {
        label: 'Notizen',
        text: 'Die Notizfelder sind am Rechner ausfüllbar und lassen sich in der App wieder einlesen; handschriftliche Einträge nicht.',
    },
];

/** Das ganze Blatt in der Reihenfolge der App, durchnummeriert je Gruppe. */
export const baueHandbuchModell = (eingang: HandbuchEingang): HandbuchModell => {
    const fortschritt = berechneUnterlagenFortschritt(eingang.alleZeilen || []);
    const name = (eingang.projektName || '').trim();
    const adresse = (eingang.projektAdresse || '').replace(/\s*\n\s*/g, ', ').trim();
    const suche = (eingang.suche || '').trim();
    const belegeBekannt = eingang.belegeBekannt !== false;

    const gruppen: HandbuchGruppe[] = gruppiereUnterlagen(eingang.zeilen || []).map((g, gi) => ({
        nummer: gi + 1,
        titel: g.titel,
        bloecke: g.zeilen.map((z, zi) =>
            handbuchBlock(z, `${gi + 1}.${String(zi + 1).padStart(2, '0')}`, eingang.belegTitel, belegeBekannt)),
    }));

    const ausgang: Record<string, string> = {};
    gruppen.forEach(g => g.bloecke.forEach(b => { ausgang[b.id] = b.notiz; }));

    return {
        titel: HANDBUCH_TITEL,
        untertitel: HANDBUCH_UNTERTITEL,
        standIso: eingang.standIso,
        kopf: {
            bauvorhaben: [name, adresse].filter(Boolean).join(', '),
            projektNummer: (eingang.projektNummer || '').trim(),
            bauherr: (eingang.bauherr || '').trim(),
            stand: datumDeutsch(eingang.standIso),
            aufgestelltVon: (eingang.aufgestelltVon || '').trim(),
            ansicht: `${FILTER_TEXT[eingang.filter] || eingang.filter}${suche ? `, Suche „${suche}"` : ''}`,
        },
        zaehlung: {
            relevant: fortschritt.relevant,
            liegtVor: fortschritt.liegtVor,
            angefordert: fortschritt.angefordert,
            offen: fortschritt.offen,
            entfaellt: fortschritt.nichtErforderlich,
            erforderlichNichtAngefordert: fortschritt.erforderlichOffen.length,
        },
        legende: handbuchLegende(),
        gruppen,
        meta: {
            format: HANDBUCH_FORMAT,
            phaseId: eingang.phaseId,
            projectId: eingang.projectId,
            stand: eingang.standIso,
        },
        ausgang,
    };
};

/** `Handbuch-Unterlagen-<Projektnr>-<JJJJ-MM-TT>.pdf`, ohne Sonderzeichen. */
export const handbuchDateiname = (projektNummer: string | undefined, projektName: string | undefined, standIso: string): string => {
    const sauber = (s: string) => (s || '')
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const kennung = sauber(projektNummer || '') || sauber(projektName || '') || 'Projekt';
    return `Handbuch-Unterlagen-${kennung}-${(standIso || '').slice(0, 10) || 'ohne-Datum'}.pdf`;
};

// ═══════════════════════════════════════════════════════════════════════════
// EINLESEN — Pruefung und Abgleich
// ═══════════════════════════════════════════════════════════════════════════

/** Was aus einem Handbuch-PDF gelesen wurde (siehe `liesHandbuchPdf`). */
export interface HandbuchGelesen {
    meta: HandbuchMeta | null;
    ausgang: Record<string, string> | null;
    notizen: { feld: string; id: string; text: string }[];
    /** Felder, die weder Notiz noch Metadaten sind — werden ignoriert. */
    unbekannteFelder: string[];
}

/**
 * Feste Grenzen beim Einlesen. Ein Handbuch ist ein paar hundert KB gross und
 * hat hoechstens so viele Notizfelder wie der Katalog Unterlagen; alles weit
 * darueber ist die falsche Datei oder eine manipulierte — und wuerde den
 * Browser (ohne Worker) lange blockieren.
 *
 * Eine zu lange Notiz wird ZURUECKGEWIESEN, nicht gekuerzt: eine gekuerzte
 * Notiz landete als Teiltext in der Akte, und wer die Vorschau nur
 * ueberfliegt, merkte es nie. 20 000 Zeichen sind rund zehn Seiten — wer
 * so viel schreibt, gehoert in ein Dokument der Ablage, nicht in ein
 * Notizfeld.
 */
export const HANDBUCH_GRENZEN = {
    dateiBytes: 25 * 1024 * 1024,
    /** Alle Formularfelder zusammen, auch fremde — geprueft, BEVOR ein
     *  Name oder Wert gelesen wird. */
    felderGesamt: 5000,
    notizen: 2000,
    notizZeichen: 20000,
    metaZeichen: 4000,
    ausgangZeichen: 4000000,
} as const;

/** Ist ein Feld eines, dessen WERT gelesen wird? Fremde Werte nie. */
export const istHandbuchFeld = (name: string): boolean =>
    name === FELD_META || name === FELD_AUSGANG || String(name || '').startsWith(NOTIZ_PRAEFIX);

/** Zu viele Formularfelder? `null` = in Ordnung. */
export const pruefeFeldanzahl = (anzahl: number): string | null =>
    anzahl > HANDBUCH_GRENZEN.felderGesamt
        ? `Das PDF hat mehr als ${HANDBUCH_GRENZEN.felderGesamt.toLocaleString('de-DE')} Formularfelder — das ist kein Handbuch der Checkliste. Bitte ein neu erzeugtes Handbuch verwenden.`
        : null;

/**
 * Grenze fuer EINEN gelesenen Feldwert — gleich beim Lesen geprueft, damit
 * nach dem ersten Verstoss nichts weiter gelesen wird. `null` = in Ordnung.
 */
export const pruefeFeldwert = (name: string, text: string): string | null => {
    if (name === FELD_META || name === FELD_AUSGANG) {
        const grenze = name === FELD_META ? HANDBUCH_GRENZEN.metaZeichen : HANDBUCH_GRENZEN.ausgangZeichen;
        return text.length > grenze
            ? `Das Feld „${name}" ist unplausibel groß — das Handbuch wurde verändert. Bitte ein neu erzeugtes Handbuch verwenden.`
            : null;
    }
    if (text.length > HANDBUCH_GRENZEN.notizZeichen) {
        const id = idAusNotizFeld(name) || name;
        return `Die Notiz zu „${id}" ist länger als ${HANDBUCH_GRENZEN.notizZeichen.toLocaleString('de-DE')} Zeichen. `
            + 'Bitte im PDF kürzen (längere Texte gehören als Dokument in die Ablage) und erneut einlesen. Es wurde nichts übernommen.';
    }
    return null;
};

/** Dateigroesse VOR dem Lesen pruefen. `null` = in Ordnung. */
export const pruefeHandbuchDatei = (groesseBytes: number): string | null => {
    if (!(groesseBytes > 0)) return 'Die Datei ist leer.';
    if (groesseBytes > HANDBUCH_GRENZEN.dateiBytes) {
        const mb = (groesseBytes / (1024 * 1024)).toFixed(1).replace('.', ',');
        return `Die Datei ist zu groß (${mb} MB, höchstens 25 MB). Ein Handbuch ist deutlich kleiner — bitte die richtige Datei wählen.`;
    }
    return null;
};

/** Liest das JSON des Metadatenfelds; alles Unbrauchbare wird `null`. */
export const leseMeta = (roh: string | undefined | null): HandbuchMeta | null => {
    if (!roh || roh.length > HANDBUCH_GRENZEN.metaZeichen) return null;
    try {
        const o = JSON.parse(roh) as unknown;
        if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
        const r = o as Record<string, unknown>;
        return {
            format: typeof r.format === 'number' ? r.format : Number.NaN,
            phaseId: typeof r.phaseId === 'string' ? r.phaseId : '',
            projectId: typeof r.projectId === 'string' ? r.projectId : '',
            stand: typeof r.stand === 'string' ? r.stand : '',
        };
    } catch {
        return null;
    }
};

/**
 * Liest den Ausgangsstand. `null` bei kaputtem JSON, falscher Form, zu
 * vielen Eintraegen oder zu langen Texten — ein Ausgangsstand, dem man nicht
 * trauen kann, taugt nicht fuer den Abgleich.
 */
export const leseAusgang = (roh: string | undefined | null): Record<string, string> | null => {
    if (!roh || roh.length > HANDBUCH_GRENZEN.ausgangZeichen) return null;
    try {
        const o = JSON.parse(roh) as unknown;
        if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
        const eintraege = Object.entries(o as Record<string, unknown>);
        if (eintraege.length > HANDBUCH_GRENZEN.notizen) return null;
        const out: Record<string, string> = {};
        for (const [k, v] of eintraege) {
            const text = typeof v === 'string' ? v : '';
            if (text.length > HANDBUCH_GRENZEN.notizZeichen) return null;
            out[k] = text;
        }
        return out;
    } catch {
        return null;
    }
};

/** Ein Formularfeld, wie `liesHandbuchPdf` es sieht. `text: null` = kein Textfeld. */
export interface RohFeld {
    name: string;
    text: string | null;
}

export type Lesergebnis = { status: 'ok'; wert: HandbuchGelesen } | { status: 'fehler'; fehler: string };

const NEU_ERZEUGEN = 'Bitte ein neu erzeugtes Handbuch verwenden.';

/**
 * Wertet die Formularfelder eines PDF aus — und weist es zurueck, bevor eine
 * Vorschau entsteht, wenn es nicht so aussieht, wie das Handbuch es angelegt
 * hat:
 *   - ein `notiz:`- oder Metadatenfeld, das kein Textfeld ist,
 *   - ein `notiz:`-Feldname, der sich nicht dekodieren laesst,
 *   - zwei Felder fuer DIESELBE Zeile (`notiz:abc` und `notiz:%61bc`) — sonst
 *     entschiede die Reihenfolge im PDF, welche Notiz gewinnt,
 *   - doppelte Metadatenfelder,
 *   - mehr Notizen oder laengere Texte als `HANDBUCH_GRENZEN` zulassen.
 * Fremde Felder ohne `notiz:`-Praefix werden ignoriert und nur gezaehlt.
 */
export const werteHandbuchFelderAus = (felder: readonly RohFeld[]): Lesergebnis => {
    const gelesen: HandbuchGelesen = { meta: null, ausgang: null, notizen: [], unbekannteFelder: [] };
    const zuViele = pruefeFeldanzahl((felder || []).length);
    if (zuViele) return { status: 'fehler', fehler: zuViele };
    const ids = new Set<string>();
    let metaRoh: string | null = null;
    let ausgangRoh: string | null = null;
    for (const f of felder || []) {
        const name = String(f.name || '');
        if (name === FELD_META || name === FELD_AUSGANG) {
            if (f.text === null) return { status: 'fehler', fehler: `Das Feld „${name}" ist kein Textfeld. ${NEU_ERZEUGEN}` };
            if ((name === FELD_META ? metaRoh : ausgangRoh) !== null) {
                return { status: 'fehler', fehler: `Das Feld „${name}" kommt doppelt vor — das Handbuch wurde verändert. ${NEU_ERZEUGEN}` };
            }
            const zuGross = pruefeFeldwert(name, f.text);
            if (zuGross) return { status: 'fehler', fehler: zuGross };
            if (name === FELD_META) metaRoh = f.text; else ausgangRoh = f.text;
            continue;
        }
        if (!name.startsWith(NOTIZ_PRAEFIX)) {
            gelesen.unbekannteFelder.push(name);
            continue;
        }
        if (f.text === null) {
            return { status: 'fehler', fehler: `Das Notizfeld „${name}" ist kein Textfeld. ${NEU_ERZEUGEN}` };
        }
        const id = idAusNotizFeld(name);
        if (id === null) {
            return { status: 'fehler', fehler: `Der Feldname „${name}" ist beschädigt. ${NEU_ERZEUGEN}` };
        }
        if (ids.has(id)) {
            return { status: 'fehler', fehler: `Zur Unterlage „${id}" gibt es mehrere Notizfelder — das Handbuch wurde verändert. ${NEU_ERZEUGEN}` };
        }
        if (ids.size >= HANDBUCH_GRENZEN.notizen) {
            return { status: 'fehler', fehler: `Das PDF hat mehr als ${HANDBUCH_GRENZEN.notizen} Notizfelder — so viele Unterlagen kennt die Checkliste nicht. ${NEU_ERZEUGEN}` };
        }
        const zuLang = pruefeFeldwert(name, f.text);
        if (zuLang) return { status: 'fehler', fehler: zuLang };
        ids.add(id);
        gelesen.notizen.push({ feld: name, id, text: f.text });
    }
    gelesen.meta = leseMeta(metaRoh);
    gelesen.ausgang = leseAusgang(ausgangRoh);
    return { status: 'ok', wert: gelesen };
};

/**
 * Gehoert das Handbuch zu dieser Planungsphase? `null` = ja, sonst die
 * Meldung fuer den Nutzer.
 */
export const pruefeHandbuch = (
    gelesen: HandbuchGelesen,
    soll: { phaseId: string | null; projectId: string },
): string | null => {
    if (!gelesen.meta) {
        return 'Diese Datei ist kein Handbuch der Unterlagen-Checkliste — die Kennung fehlt. Bitte das PDF verwenden, das über „Handbuch" erzeugt wurde.';
    }
    if (gelesen.meta.format !== HANDBUCH_FORMAT) {
        return `Dieses Handbuch hat eine unbekannte Fassung (${String(gelesen.meta.format)}). Bitte ein neues Handbuch erzeugen und die Notizen dort eintragen.`;
    }
    if (!soll.phaseId || gelesen.meta.phaseId !== soll.phaseId || gelesen.meta.projectId !== soll.projectId) {
        return 'Das Handbuch gehört zu einem anderen Projekt. Es wurde nichts übernommen.';
    }
    if (!gelesen.ausgang) {
        return 'Das Handbuch ist unvollständig oder beschädigt — der Ausgangsstand der Notizen fehlt oder ist nicht lesbar. Bitte ein neues Handbuch erzeugen.';
    }
    return null;
};

export type NotizAktion = 'ersetzen' | 'anhaengen' | 'ueberspringen';

/**
 * ersetzen          — nur im Handbuch geaendert
 * konflikt          — im Handbuch UND in der App geaendert
 * unveraendert      — nichts zu tun
 * nicht_zuordenbar  — die Zeile gibt es nicht mehr
 * begruendung_bleibt — „nicht erforderlich" und leere Notiz: die Begruendung bleibt Pflicht
 */
export type NotizArt = 'ersetzen' | 'konflikt' | 'unveraendert' | 'nicht_zuordenbar' | 'begruendung_bleibt';

export interface NotizVorschlag {
    id: string;
    name: string;
    art: NotizArt;
    ausgang: string;
    aktuell: string;
    neu: string;
    /** Standard-Aktion; der Nutzer kann bei `ersetzen` und `konflikt` waehlen. */
    vorschlag: NotizAktion;
    /** Die Zeile hat noch keinen gespeicherten Eintrag — er entsteht mit Status „offen". */
    neuerEintrag: boolean;
    hinweis?: string;
    /** Seit der letzten Vorschau in der App geaendert — muss erneut bestaetigt werden. */
    erneutBestaetigen?: boolean;
}

/**
 * Abgleich je Notizfeld.
 *
 *   neu == ausgang             → unveraendert
 *   neu == aktuell             → unveraendert (beide gleich geaendert)
 *   aktuell == ausgang         → ersetzen
 *   sonst                      → konflikt, Standard „anhaengen" (nichts geht
 *                                verloren); mit leerer neuer Notiz
 *                                „ueberspringen" — ein angehaengtes Nichts
 *                                waere nur Laerm
 *   „nicht erforderlich" + leer → uebersprungen, die Begruendung bleibt Pflicht
 *   Zeile fehlt               → nicht zuordenbar
 *
 * Verglichen wird normalisiert (`normalisiereNotiz`); uebernommen wird die
 * normalisierte neue Notiz.
 */
export const vergleicheHandbuchNotizen = (
    gelesen: HandbuchGelesen,
    zeilen: readonly UnterlagenZeile[],
): NotizVorschlag[] => {
    const jeId = new Map((zeilen || []).map(z => [z.id, z]));
    const ausgangJeId = gelesen.ausgang || {};
    return (gelesen.notizen || []).map(n => {
        const z = jeId.get(n.id);
        const neu = normalisiereNotiz(n.text);
        const ausgang = normalisiereNotiz(ausgangJeId[n.id]);
        if (!z) {
            return {
                id: n.id, name: n.id, art: 'nicht_zuordenbar', ausgang, aktuell: '', neu,
                vorschlag: 'ueberspringen', neuerEintrag: false,
                hinweis: 'Diese Unterlage gibt es in der Checkliste nicht mehr.',
            } as NotizVorschlag;
        }
        const aktuell = normalisiereNotiz(z.eintrag ? z.eintrag.bemerkung : '');
        const basis = { id: z.id, name: z.name, ausgang, aktuell, neu, neuerEintrag: !z.eintrag };
        if (neu === ausgang || neu === aktuell) {
            return { ...basis, art: 'unveraendert', vorschlag: 'ueberspringen' } as NotizVorschlag;
        }
        if (z.status === 'nicht_erforderlich' && !neu) {
            return {
                ...basis, art: 'begruendung_bleibt', vorschlag: 'ueberspringen',
                hinweis: 'Die Unterlage ist als nicht erforderlich begründet — die Begründung bleibt Pflicht und wird nicht geleert.',
            } as NotizVorschlag;
        }
        if (aktuell === ausgang) {
            return { ...basis, art: 'ersetzen', vorschlag: 'ersetzen' } as NotizVorschlag;
        }
        return {
            ...basis,
            art: 'konflikt',
            vorschlag: neu ? 'anhaengen' : 'ueberspringen',
            hinweis: 'Die Notiz wurde im Handbuch UND in der App geändert.',
        } as NotizVorschlag;
    });
};

/** Die Vorschlaege, bei denen etwas zu entscheiden oder zu uebernehmen ist. */
export const istHandlungsbedarf = (v: NotizVorschlag): boolean => v.art === 'ersetzen' || v.art === 'konflikt';

/** Text beim Anhaengen: `aktuell` + Zeile „— Handbuch <TT.MM.JJJJ>: neu". */
export const angehaengteNotiz = (aktuell: string, neu: string, standIso: string): string => {
    const zusatz = `— Handbuch ${datumDeutsch(standIso) || 'ohne Datum'}: ${neu}`;
    return aktuell.trim() ? `${aktuell}\n${zusatz}` : zusatz;
};

/** Die gewaehlte Aktion eines Vorschlags — `null`, wenn nichts angewandt wird. */
const gewaehlteAktion = (v: NotizVorschlag, auswahl: Readonly<Record<string, NotizAktion>>): NotizAktion | null => {
    if (!istHandlungsbedarf(v)) return null;
    const aktion = auswahl[v.id] || v.vorschlag;
    if (aktion === 'ueberspringen') return null;
    if (v.art === 'ersetzen' && aktion === 'anhaengen') return null;
    return aktion;
};

/**
 * Welche gewaehlten Aenderungen beruhen auf einem Stand, den es nicht mehr
 * gibt? Verglichen wird die Bemerkung in `liste` (dem Stand beim Schreiben)
 * mit `aktuell` aus der Vorschau. Uebersprungene Vorschlaege zaehlen nicht —
 * sie werden ohnehin nicht angewandt.
 */
export const veralteteVorschlaege = (
    liste: readonly PlanungUnterlage[],
    vorschlaege: readonly NotizVorschlag[],
    auswahl: Readonly<Record<string, NotizAktion>>,
): string[] => {
    const jeId = new Map((liste || []).map(e => [e.id, e]));
    return (vorschlaege || [])
        .filter(v => gewaehlteAktion(v, auswahl) !== null)
        .filter(v => {
            const e = jeId.get(v.id);
            return normalisiereNotiz(e ? e.bemerkung : '') !== v.aktuell;
        })
        .map(v => v.id);
};

/**
 * Wendet die gewaehlten Aktionen auf die AKTUELLE Liste an — in einem Zug,
 * damit der Schreiber genau EINEN Stand schreibt.
 *
 * KEINE UEBERNAHME AUF VERALTETER GRUNDLAGE: Hat sich eine betroffene
 * Bemerkung seit der Vorschau geaendert (`veralteteVorschlaege`), wird GAR
 * NICHTS angewandt — auch nicht die unveraenderten Eintraege. Die Liste kommt
 * unveraendert zurueck, `veraltet` nennt die Eintraege; der Aufrufer rechnet
 * mit `vergleicheErneut` eine neue Vorschau. Sonst ersetzte die alte
 * Entscheidung („ersetzen") still, was ein Kollege inzwischen eingetragen
 * hat, und eine Teiluebernahme haette niemand bestaetigt.
 *
 * Grundlage je Zeile ist der Eintrag in `liste`; fehlt er, entsteht er aus
 * der Zeile mit Status „offen". Die Bemerkung wird ueber
 * `setzeUnterlagenBemerkung` gesetzt, damit dieselben Regeln gelten wie beim
 * Tippen in der App (Begruendung bei „nicht erforderlich" nie leer).
 */
export const uebernehmeHandbuchNotizen = (
    liste: readonly PlanungUnterlage[],
    zeilen: readonly UnterlagenZeile[],
    vorschlaege: readonly NotizVorschlag[],
    auswahl: Readonly<Record<string, NotizAktion>>,
    standIso: string,
): { liste: PlanungUnterlage[]; uebernommen: number; abgelehnt: string[]; veraltet: string[] } => {
    const veraltet = veralteteVorschlaege(liste, vorschlaege, auswahl);
    if (veraltet.length) return { liste: [...(liste || [])], uebernommen: 0, abgelehnt: [], veraltet };

    const jeId = new Map((zeilen || []).map(z => [z.id, z]));
    let neu: PlanungUnterlage[] = [...(liste || [])];
    let uebernommen = 0;
    const abgelehnt: string[] = [];
    (vorschlaege || []).forEach(v => {
        const aktion = gewaehlteAktion(v, auswahl);
        if (!aktion) return;
        const z = jeId.get(v.id);
        if (!z) return;
        const vorhanden = neu.find(e => e.id === v.id);
        const basis = vorhanden ? { ...vorhanden } : eintragFuerZeile({ ...z, eintrag: null });
        const text = aktion === 'anhaengen' ? angehaengteNotiz(basis.bemerkung || '', v.neu, standIso) : v.neu;
        const r = setzeUnterlagenBemerkung(basis, text);
        if (r.status !== 'ok') { abgelehnt.push(v.name); return; }
        if (r.wert === basis && vorhanden) return;
        neu = uebernehmeUnterlage(neu, r.wert);
        uebernommen += 1;
    });
    return { liste: neu, uebernommen, abgelehnt, veraltet: [] };
};

/** Die Zeilen mit den Eintraegen aus `liste` — fuer einen Abgleich gegen
 *  einen Stand, der juenger ist als die gezeichneten Zeilen. */
export const zeilenMitStand = (
    zeilen: readonly UnterlagenZeile[],
    liste: readonly PlanungUnterlage[],
): UnterlagenZeile[] => {
    const jeId = new Map((liste || []).map(e => [e.id, e]));
    return (zeilen || []).map(z => {
        const e = jeId.get(z.id) || null;
        return { ...z, eintrag: e, status: e ? e.status : 'offen' };
    });
};

export const ERNEUT_BESTAETIGEN = 'In der App inzwischen geändert — bitte erneut bestätigen.';

/**
 * Neue Vorschau nach einer abgelehnten Uebernahme: Abgleich gegen den Stand
 * `liste`. Die veralteten Eintraege werden — sofern noch etwas zu tun ist —
 * zu Konflikten mit Standard „anhaengen" (nichts geht verloren) und tragen
 * den Hinweis, dass sie erneut zu bestaetigen sind.
 */
export const vergleicheErneut = (
    gelesen: HandbuchGelesen,
    zeilen: readonly UnterlagenZeile[],
    liste: readonly PlanungUnterlage[],
    veraltet: readonly string[],
): NotizVorschlag[] => {
    const markiert = new Set(veraltet || []);
    return vergleicheHandbuchNotizen(gelesen, zeilenMitStand(zeilen, liste)).map(v => {
        if (!markiert.has(v.id) || !istHandlungsbedarf(v)) return v;
        return {
            ...v,
            art: 'konflikt',
            vorschlag: v.neu ? 'anhaengen' : 'ueberspringen',
            hinweis: ERNEUT_BESTAETIGEN,
            erneutBestaetigen: true,
        } as NotizVorschlag;
    });
};
